import React from 'react';
import Popover from 'material-ui/Popover';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import './GenericTooltip.styl';

/**
 * GenericTooltip is a component to easily paint a React.Component with a
 * material UI Popover.  We'll accept any type of component and attach a popover
 * with the specified internals
 */
export default class GenericTooltip extends React.PureComponent {
  static propTypes = {
    // hover behavior gives tooltip functionality.
    // we'll expand and keep the Popover open until the user mouses off of the *target* or *tooltip*
    hoverBehavior: PropTypes.bool,
    // click behavior gives functionality like a dropdown. Wait until a click to close.
    // we'll expand and keep the Popover open until the user mouses out of the *Popover*
    // Note that in this case, we need to be able to pass onMouseLeave down
    clickBehavior: PropTypes.bool,
    // should a click close the tooltip? default behavior is true
    clickToClose: PropTypes.bool,
    // should a blur off the tooltip close? default behavior is true
    blurToClose: PropTypes.bool,
    // positionTop: shorthand to translate to the right anchor/target under the covers
    positionTop: PropTypes.bool,
    // positionBottom: same as above
    positionBottom: PropTypes.bool,
    // anchor to the left
    positionLeft: PropTypes.bool,
    // anchor to the right
    positionRight: PropTypes.bool,
    // anchorOrigin/targetOrigin match material's Popover params
    anchorOrigin: PropTypes.object,
    targetOrigin: PropTypes.object,
    // what will be rendered in the tooltip (bool is allowed for ease of creating falsy conditionals)
    tooltip: PropTypes.oneOfType([PropTypes.element, PropTypes.string, PropTypes.bool]),
    // what will be rendered on the page
    children: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    // callback invoked when the tooltip is shown
    onOpen: PropTypes.func,
    // callback invoked when the tooltip is closed
    onClose: PropTypes.func,
    // classname for the menu wrapper
    className: PropTypes.string,
    // delay - how long you need to hover on the anchor in order to pop it up, if any
    delay: PropTypes.number,
    // close immediately - if hover behavior, we won't let a tooltip stay open when
    // the tooltip is hovered. the default behavior is to keep it open.
    closeImmediately: PropTypes.bool,
    // make controlled component you control whether the tooltip is open
    open: PropTypes.bool,
    inline: PropTypes.bool,
  };

  constructor(props) {
    super(props);

    this.state = {
      showTooltip: false,
      hoveredOnTarget: undefined,
      hoveredOnTooltip: undefined,
    };

    this.onOpen = this.onOpen.bind(this);
    this.onClose = this.onClose.bind(this);
    this.maybeClose = this.maybeClose.bind(this);
    this.onTargetLeave = this.onTargetLeave.bind(this);
    this.onTooltipLeave = this.onTooltipLeave.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
  }

  componentDidMount() {
    const {open} = this.props;

    if (open) {
      this.props.onOpen && this.props.onOpen();
      this.setState({showTooltip: true});
    }
  }

  onOpen() {
    const {onOpen} = this.props;
    this.setState({
      showTooltip: true,
      hoveredOnTarget: true,
      hoveredOnTooltip: false,
    });
    if (this.props.clickBehavior) {
      document.addEventListener('click', this.onDocumentClick);
    }
    onOpen && onOpen();
  }

  onDocumentClick(e) {
    this.popoverRef && !ReactDOM.findDOMNode(this.popoverRef).contains(e.target) && this.onClose();
  }

  onTooltipLeave() {
    if (this.props.blurToClose === false) {
      return;
    }

    if (this.props.closeImmediately) {
      this.onClose();
    } else {
      if (this.props.clickBehavior) {
        this.onClose();
      } else {
        this.setState({hoveredOnTooltip: false}, () => setTimeout(this.maybeClose, 100));
      }
    }
  }

  onTargetEnter() {
    const {delay} = this.props;
    if (!delay) {
      this.onOpen();
    } else {
      this.quietPeriodBeforeOpen = true;
      setTimeout(() => {
        if (this.quietPeriodBeforeOpen) {
          this.onOpen();
        }
      }, delay);
    }
  }

  onTargetLeave() {
    this.quietPeriodBeforeOpen = false;

    if (this.props.blurToClose === false) {
      return;
    }

    if (this.props.closeImmediately) {
      this.onClose();
    } else {
      this.setState({hoveredOnTarget: false}, () => setTimeout(this.maybeClose, 100));
    }
  }

  maybeClose() {
    const {hoveredOnTarget, hoveredOnTooltip} = this.state;

    if (!hoveredOnTarget && !hoveredOnTooltip) {
      this.onClose();
    }
  }

  onClose() {
    if (this.props.open) return;

    this.quietPeriodBeforeOpen = false;
    document.removeEventListener('click', this.onDocumentClick);
    this.setState({showTooltip: false});

    this.props.onClose && this.props.onClose();
  }

  getPositioning(props) {
    const {positionTop, positionBottom, positionRight, positionLeft, anchorOrigin, targetOrigin} =
      props;

    if (positionTop) {
      return [
        {horizontal: 'middle', vertical: 'top'},
        {horizontal: 'middle', vertical: 'bottom'},
      ];
    }

    if (positionBottom) {
      return [
        {horizontal: 'middle', vertical: 'bottom'},
        {horizontal: 'middle', vertical: 'top'},
      ];
    }

    if (positionRight) {
      return [
        {horizontal: 'right', vertical: 'center'},
        {horizontal: 'left', vertical: 'center'},
      ];
    }

    if (positionLeft) {
      return [
        {horizontal: 'left', vertical: 'center'},
        {horizontal: 'right', vertical: 'center'},
      ];
    }

    return [
      anchorOrigin || {
        horizontal: 'left',
        vertical: 'bottom',
      },
      targetOrigin || {horizontal: 'left', vertical: 'top'},
    ];
  }

  render() {
    let {inline, hoverBehavior, hoveredOnTooltip, clickBehavior, clickToClose, className, tooltip} =
      this.props;
    const {showTooltip} = this.state;

    if (!hoverBehavior && !clickBehavior) {
      hoverBehavior = true;
    }

    const [anchorOrigin, targetOrigin] = this.getPositioning(this.props);

    return (
      <div className={cn('generic-tooltip-wrapper', {inline})}>
        <div
          className="help"
          onMouseEnter={() => hoverBehavior && this.onTargetEnter()}
          onMouseLeave={() => hoverBehavior && this.onTargetLeave()}
          onClick={() => {
            if (clickBehavior) {
              showTooltip ? this.onClose() : this.onOpen();
            }
          }}
          ref={ref => (this.helpRef = ref)}>
          {this.props.children}
        </div>
        {tooltip && showTooltip && (
          <Popover
            open={true}
            className={className}
            useLayerForClickAway={false}
            ref={r => (this.popoverRef = r)}
            anchorEl={this.helpRef}
            anchorOrigin={anchorOrigin}
            targetOrigin={targetOrigin}>
            <div
              className={'help-popover'}
              onClick={
                clickBehavior
                  ? (clickToClose === undefined || clickToClose) && this.onClose
                  : undefined
              }
              onMouseLeave={this.onTooltipLeave}
              onMouseEnter={() => !hoveredOnTooltip && this.setState({hoveredOnTooltip: true})}
              onMouseOver={() => !hoveredOnTooltip && this.setState({hoveredOnTooltip: true})}>
              {tooltip}
            </div>
          </Popover>
        )}
      </div>
    );
  }
}
